"use client";

import classnames from "classnames";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";

import type { NavGroup, NavItem } from "./types";
import { Icon } from "../icon/icon";
import styles from "./sidebar.module.scss";

interface SidebarNavProps {
  items: NavItem[];
  groups?: NavGroup[];
  activeItem: string;
  onNavigate: (href: string) => void;
  collapsed: boolean;
}

const SIDEBAR_COLLAPSED_WIDTH = 56;
const STORAGE_KEY = "sidebar-collapsed-groups";

function loadCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveCollapsedGroups(set: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // Quota exceeded — ignore
  }
}

function NavItemButton({
  item,
  isActive,
  labelDelay,
  onNavigate,
  showTooltip,
  hideTooltip,
}: {
  item: NavItem;
  isActive: boolean;
  labelDelay: number;
  onNavigate: (href: string) => void;
  showTooltip: (e: React.MouseEvent<HTMLButtonElement>, label: string) => void;
  hideTooltip: () => void;
}) {
  return (
    <button
      key={item.id}
      type="button"
      className={classnames(styles.navItem, isActive && styles.navItemActive)}
      onClick={() => onNavigate(item.href)}
      aria-current={isActive ? "page" : undefined}
      onMouseEnter={(e) => showTooltip(e, item.label)}
      onMouseLeave={hideTooltip}
    >
      <span className={styles.navIcon}>{item.icon}</span>
      <span className={styles.navLabel} style={{ transitionDelay: `${labelDelay}ms` }}>
        {item.label}
      </span>
      {item.badge !== undefined && item.badge > 0 && (
        <span className={styles.navBadge}>{item.badge}</span>
      )}
    </button>
  );
}

export function SidebarNav({ items, groups, activeItem, onNavigate, collapsed }: SidebarNavProps) {
  const [tooltip, setTooltip] = useState<{ label: string; top: number } | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState(loadCollapsedGroups);

  const showTooltip = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, label: string) => {
      if (!collapsed) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({ label, top: rect.top + rect.height / 2 });
    },
    [collapsed],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      saveCollapsedGroups(next);
      return next;
    });
  }, []);

  const itemMap = new Map(items.map((item) => [item.id, item]));

  const renderItem = (item: NavItem, index: number) => (
    <div key={item.id} className={item.subItems ? styles.navBranch : undefined}>
      <div className={item.subItems ? styles.navParent : undefined}>
        <NavItemButton
          key={item.id}
          item={item}
          isActive={
            item.id === activeItem ||
            (collapsed && Boolean(item.subItems?.some((child) => child.id === activeItem)))
          }
          labelDelay={collapsed ? 0 : 80 + index * 20}
          onNavigate={onNavigate}
          showTooltip={showTooltip}
          hideTooltip={hideTooltip}
        />
        {item.subItems && (
          <button
            type="button"
            className={classnames(styles.branchToggle, collapsed && styles.branchToggleHidden)}
            aria-hidden={collapsed || undefined}
            tabIndex={collapsed ? -1 : undefined}
            aria-label={`${item.expanded ? "Collapse" : "Expand"} ${item.label}`}
            aria-expanded={Boolean(item.expanded)}
            aria-controls={`sidebar-children-${item.id}`}
            onClick={item.onToggle}
          >
            <span aria-hidden="true">
              <Icon name={item.expanded ? "chevron-down" : "chevron-right"} />
            </span>
          </button>
        )}
      </div>
      {item.subItems && (
        <div
          id={`sidebar-children-${item.id}`}
          className={styles.navChildren}
          aria-hidden={collapsed || undefined}
          inert={collapsed}
          hidden={!item.expanded}
        >
          {item.subItems.map((child) => (
            <NavItemButton
              key={child.id}
              item={child}
              isActive={child.id === activeItem}
              labelDelay={0}
              onNavigate={onNavigate}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
            />
          ))}
        </div>
      )}
    </div>
  );

  // Flat rendering when no groups provided
  if (!groups) {
    return (
      <nav className={styles.nav} aria-label="Main navigation">
        {items.map((item, index) => renderItem(item, index))}
        {tooltip &&
          createPortal(
            <div
              className={styles.navTooltip}
              style={{ top: tooltip.top, left: SIDEBAR_COLLAPSED_WIDTH + 8 }}
            >
              {tooltip.label}
            </div>,
            document.body,
          )}
      </nav>
    );
  }

  // Grouped rendering
  let globalIndex = 0;

  return (
    <nav className={styles.nav} aria-label="Main navigation">
      {groups.map((group) => {
        const groupItems = group.itemIds
          .map((id) => itemMap.get(id))
          .filter((item): item is NavItem => item !== undefined);
        if (groupItems.length === 0) return null;

        const isGroupCollapsed = collapsedGroups.has(group.id);
        const hasActiveItem = groupItems.some((item) => item.id === activeItem);

        return (
          <div key={group.id} className={styles.navGroup}>
            <button
              type="button"
              className={classnames(
                styles.navGroupHeader,
                collapsed && styles.navGroupHeaderHidden,
              )}
              onClick={() => toggleGroup(group.id)}
              aria-expanded={!isGroupCollapsed}
            >
              <span className={styles.navGroupLabel}>{group.label}</span>
              <span
                className={classnames(
                  styles.navGroupChevron,
                  isGroupCollapsed && styles.navGroupChevronCollapsed,
                )}
              >
                ›
              </span>
            </button>
            {(!isGroupCollapsed || hasActiveItem) &&
              groupItems.map((item) => {
                // Always show active item even if group is collapsed
                if (isGroupCollapsed && item.id !== activeItem) return null;
                const idx = globalIndex++;
                return renderItem(item, idx);
              })}
          </div>
        );
      })}
      {tooltip &&
        createPortal(
          <div
            className={styles.navTooltip}
            style={{ top: tooltip.top, left: SIDEBAR_COLLAPSED_WIDTH + 8 }}
          >
            {tooltip.label}
          </div>,
          document.body,
        )}
    </nav>
  );
}
